/**
 * components/skeleton.tsx — loading placeholders shaped like the real content.
 *
 * The console's previous loading state was a centred spinner that replaced the
 * whole screen. Two problems: the layout jumps when the spinner is swapped for
 * content, and a spinner communicates "wait" without communicating "what for".
 *
 * A skeleton that occupies the same box as the thing it stands in for fixes
 * both — the page is laid out correctly from the first frame, and the shapes
 * tell you what is arriving. Perceived load time drops even when the actual
 * numbers are identical.
 *
 * The pulse is deliberately slow and low-contrast: a skeleton should recede,
 * not compete with the content that lands next to it.
 */

import React, { useEffect, useRef } from 'react';
import { View, Animated, Easing, Platform, ViewStyle } from 'react-native';

import { useTheme } from '../context/themeContext';

const prefersReducedMotion =
  Platform.OS === 'web' && typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

/**
 * One pulsing block. Give it the width/height of the text or element it
 * replaces so nothing shifts when the real content swaps in.
 */
export function SkeletonBlock({ width, height = 12, radius = 6, style }: {
  width?: number | `${number}%`;
  height?: number;
  radius?: number;
  style?: ViewStyle;
}) {
  const c = useTheme().theme.colors;
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (prefersReducedMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 900, easing: Easing.inOut(Easing.quad), useNativeDriver: false }),
        Animated.timing(pulse, { toValue: 0, duration: 900, easing: Easing.inOut(Easing.quad), useNativeDriver: false }),
      ]),
    );
    loop.start();
    return () => { loop.stop(); };
  }, [pulse]);

  // Between the page and the hairline colour — visible as "something goes
  // here", never mistakable for real content.
  const opacity = prefersReducedMotion
    ? 0.6
    : pulse.interpolate({ inputRange: [0, 1], outputRange: [0.45, 0.9] });

  return (
    <Animated.View
      style={[{ width: width ?? '100%', height, borderRadius: radius, backgroundColor: c.line, opacity }, style as object]}
    />
  );
}

/**
 * Fades children in once they exist. Pairs with the skeletons: content arrives
 * softly instead of snapping, which is what makes a fast screen read as
 * composed rather than jumpy.
 */
export function FadeIn({ children, style }: { children: React.ReactNode; style?: ViewStyle }) {
  const opacity = useRef(new Animated.Value(prefersReducedMotion ? 1 : 0)).current;

  useEffect(() => {
    if (prefersReducedMotion) return;
    Animated.timing(opacity, {
      toValue: 1,
      duration: 220,
      easing: Easing.out(Easing.quad),
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [opacity]);

  return <Animated.View style={[{ opacity }, style as object]}>{children}</Animated.View>;
}

/** Placeholder shaped like a KpiCard: label line, big value line, sub line. */
export function KpiSkeleton() {
  const t = useTheme().theme;
  const c = t.colors;
  return (
    <View style={{ backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10, padding: 15, paddingVertical: 14, ...(t.shadows.sm as object) }}>
      <SkeletonBlock width={62} height={9} radius={4} style={{ marginBottom: 11 }} />
      <SkeletonBlock width={54} height={20} radius={5} />
      <SkeletonBlock width={78} height={9} radius={4} style={{ marginTop: 9 }} />
    </View>
  );
}

/** Placeholder for one list row (recent calls / live now). */
export function RowSkeleton({ last = false }: { last?: boolean }) {
  const c = useTheme().theme.colors;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: last ? 0 : 1, borderBottomColor: c.line }}>
      <SkeletonBlock width={40} height={9} radius={4} />
      <SkeletonBlock width={13} height={9} radius={4} />
      <SkeletonBlock width={96} height={10} radius={4} />
      <View style={{ flex: 1 }}><SkeletonBlock height={10} radius={4} /></View>
      <SkeletonBlock width={64} height={10} radius={4} />
    </View>
  );
}
